/**
 * ExternalPlanCTAV2
 *
 * Call-to-action card encouraging users to declare if they have
 * external individual pension plans. This helps optimize their
 * contribution limits and tax benefits.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { PiggyBank, ArrowRight, Info } from 'lucide-react';
import { Button } from '../ui/button';
import ResponsiveTooltip from '../ui/responsive-tooltip';

interface ExternalPlanCTAV2Props {
	hasExternalPlan?: boolean;
	externalPlanAmount?: number;
	onConfigureClick: () => void;
	translate: (key: string) => string;
}

const ExternalPlanCTAV2: React.FC<ExternalPlanCTAV2Props> = ({
	hasExternalPlan,
	externalPlanAmount,
	onConfigureClick,
	translate
}) => {
	// If user already has declared an external plan, show summary
	if (hasExternalPlan && externalPlanAmount !== undefined && externalPlanAmount > 0) {
		return (
			<motion.div
				initial={{ opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				className="bg-white rounded-xl border border-gray-200 shadow-xs p-5"
			>
				<div className="flex items-start gap-4">
					<div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
						<PiggyBank className="w-5 h-5 text-secondary" />
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-1.5 mb-1">
							<h3 className="text-sm font-semibold text-gray-900">
								{translate('user.externalPlan.title')}
							</h3>
							<ResponsiveTooltip content={translate('user.externalPlan.tooltip')}>
								<button type="button" className="inline-flex cursor-help">
									<Info className="w-3.5 h-3.5 text-gray-400" />
								</button>
							</ResponsiveTooltip>
						</div>
						<p className="text-sm text-gray-600">
							{translate('user.externalPlan.declared')}{' '}
							<span className="font-semibold text-secondary">{externalPlanAmount}€</span>{' '}
							{translate('user.externalPlan.thisYear')}
						</p>
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={onConfigureClick}
						className="text-primary hover:text-primary/80 shrink-0"
					>
						{translate('common.edit')}
					</Button>
				</div>
			</motion.div>
		);
	}

	// CTA to declare external plan
	return (
		<motion.div
			initial={{ opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			className="bg-linear-to-r from-secondary/5 to-primary/5 rounded-xl border border-secondary/20 p-5"
		>
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
				<div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center shrink-0">
					<PiggyBank className="w-6 h-6 text-secondary" />
				</div>
				<div className="flex-1 min-w-0">
					<h3 className="text-base font-semibold text-gray-900 mb-1">
						{translate('user.externalPlan.ctaTitle')}
					</h3>
					<p className="text-sm text-gray-600">{translate('user.externalPlan.ctaDescription')}</p>
				</div>
				<Button
					onClick={onConfigureClick}
					className="bg-secondary hover:bg-secondary/90 text-white gap-2 shrink-0 w-full sm:w-auto"
				>
					{translate('user.externalPlan.ctaButton')}
					<ArrowRight className="w-4 h-4" />
				</Button>
			</div>
		</motion.div>
	);
};

export default ExternalPlanCTAV2;
